import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  ArrowRight,
  CheckCircle2,
  Lock,
  PlayCircle,
  Shield,
  Sparkles,
  UserCheck,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import BeforeYouJoinFAQ from './BeforeYouJoinFAQ';
import BBCQuickListBrandHero from '../branding/BBCQuickListBrandHero';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  bg: '#0c1117',
  bgAlt: '#121821',
  card: '#171f2b',
  border: '#1f2937',
  text: '#f8fafc',
  textMuted: '#94a3b8',
  accent: '#eab308',
  accentSoft: 'rgba(234,179,8,0.12)',
  success: '#22c55e',
};

const SectionWrap = ({ children, alt }) => (
  <section
    className="py-12 md:py-20"
    style={{ backgroundColor: alt ? COLORS.bgAlt : COLORS.bg }}
  >
    <div className="max-w-3xl md:max-w-4xl mx-auto px-4 md:px-6">{children}</div>
  </section>
);

const TouchPanel = ({ touch, badge, onContinue, isFinal, formRef, dataTestId }) => {
  if (!touch) return null;
  return (
    <div
      className="rounded-2xl p-6 md:p-10 border"
      style={{
        backgroundColor: COLORS.card,
        borderColor: COLORS.border,
      }}
      data-testid={dataTestId}
    >
      <Badge
        className="px-3 py-1 mb-4"
        style={{
          backgroundColor: COLORS.accentSoft,
          color: COLORS.accent,
          border: `1px solid ${COLORS.accent}40`,
        }}
      >
        {badge}
      </Badge>
      <h2
        className="text-3xl md:text-4xl font-bold leading-tight mb-3"
        style={{ color: COLORS.text }}
      >
        {touch.headline}
      </h2>
      {touch.subheadline && (
        <p className="text-base md:text-lg mb-6" style={{ color: COLORS.textMuted }}>
          {touch.subheadline}
        </p>
      )}

      {touch.video_url ? (
        <div
          className="aspect-video rounded-xl overflow-hidden mb-6"
          style={{ backgroundColor: '#000' }}
        >
          {/youtube|youtu\.be|vimeo|mediadelivery\.net|player\.bunny/i.test(touch.video_url) ? (
            <iframe
              src={touch.video_url
                .replace('watch?v=', 'embed/')
                .replace('youtu.be/', 'www.youtube.com/embed/')}
              title="Touch video"
              loading="lazy"
              className="w-full h-full"
              style={{ border: 0 }}
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          ) : (
            <video src={touch.video_url} controls playsInline preload="auto" className="w-full h-full" />
          )}
        </div>
      ) : (
        <div
          className="aspect-video rounded-xl flex items-center justify-center mb-6 border"
          style={{
            backgroundColor: COLORS.bgAlt,
            borderColor: COLORS.border,
          }}
        >
          <div className="text-center" style={{ color: COLORS.textMuted }}>
            <PlayCircle className="h-10 w-10 mx-auto mb-2" />
            <p className="text-sm">Video coming soon</p>
          </div>
        </div>
      )}

      {touch.body_text && (
        <p
          className="text-base md:text-lg leading-relaxed mb-6 whitespace-pre-line"
          style={{ color: COLORS.text }}
        >
          {touch.body_text}
        </p>
      )}

      <div className="flex justify-end">
        {isFinal ? (
          <Button
            onClick={() => formRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="px-6 py-5 text-base font-semibold rounded-xl"
            style={{ backgroundColor: COLORS.accent, color: COLORS.bg }}
            data-testid={`${dataTestId}-cta`}
          >
            {touch.cta_button_text || 'Reserve My Spot'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={onContinue}
            className="px-6 py-5 text-base font-semibold rounded-xl"
            style={{ backgroundColor: COLORS.accent, color: COLORS.bg }}
            data-testid={`${dataTestId}-cta`}
          >
            {touch.cta_button_text || 'Continue'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

const WaitlistLaunchPage = () => {
  const { code: paramCode } = useParams();
  const navigate = useNavigate();
  const formRef = useRef(null);

  // Also support ?ref=
  const queryRef = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('ref');
  }, []);
  const referralCode = paramCode || queryRef || null;

  const [config, setConfig] = useState(null);
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Touch reveal state — start showing Touch 2 only
  const [revealed, setRevealed] = useState({ t3: false, t4: false });

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone: '',
    consent_communications: true,
    wallet_trc20: '',
    needs_wallet_help: false,
  });

  const [submitted, setSubmitted] = useState(null); // {signup_id, my_referral_code, referral_link, waitlist_token}

  useEffect(() => {
    (async () => {
      try {
        const url = `${BACKEND_URL}/api/public/waitlist/config${
          referralCode ? `?ref=${encodeURIComponent(referralCode)}` : ''
        }`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.ok) {
          setConfig(data.config);
          setReferrer(data.referrer && data.referrer.valid ? data.referrer : null);
        }
      } catch (e) {
        console.error('waitlist config fetch failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [referralCode]);

  const continueToT3 = () => {
    setRevealed((r) => ({ ...r, t3: true }));
    setTimeout(() => {
      document.getElementById('touch-3')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };
  const continueToT4 = () => {
    setRevealed((r) => ({ ...r, t4: true }));
    setTimeout(() => {
      document.getElementById('touch-4')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.consent_communications) {
      toast.error('Please agree to receive updates so we can hold your spot.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        mobile_phone: form.mobile_phone.trim(),
        consent_communications: form.consent_communications,
        wallet_trc20: form.wallet_trc20.trim() || null,
        needs_wallet_help: form.needs_wallet_help,
        referral_code: referralCode || null,
        landing_url: window.location.pathname,
      };

      const res = await fetch(`${BACKEND_URL}/api/public/waitlist/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        toast.error(data.detail || data.error || 'Could not save your spot. Please try again.');
        return;
      }
      // Persist limited-dashboard token
      try {
        localStorage.setItem('bbc_waitlist_token', data.waitlist_token);
        localStorage.setItem(
          'bbc_waitlist_signup',
          JSON.stringify({
            signup_id: data.signup_id,
            my_referral_code: data.my_referral_code,
            referral_link: data.referral_link,
          })
        );
      } catch {
        // ignore localStorage write errors (private mode etc.)
      }
      setSubmitted(data);
      toast.success('You\'re on the list, builder.');
    } catch (err) {
      console.error('submit error', err);
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Loading state ----------
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: COLORS.bg, color: COLORS.text }}
      >
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: COLORS.accent }} />
      </div>
    );
  }

  // ---------- Waitlist closed ----------
  if (config && !config.waitlist_enabled) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: COLORS.bg }}
      >
        <Card className="max-w-md w-full border-0" style={{ backgroundColor: COLORS.card }}>
          <CardContent className="p-8 text-center">
            <Lock className="h-10 w-10 mx-auto mb-4" style={{ color: COLORS.accent }} />
            <h2 className="text-2xl font-bold mb-3" style={{ color: COLORS.text }}>
              The waiting list is currently closed
            </h2>
            <p className="mb-6" style={{ color: COLORS.textMuted }}>
              We'll reopen it soon. Check back in shortly.
            </p>
            <Link to="/">
              <Button style={{ backgroundColor: COLORS.accent, color: COLORS.bg }}>
                Back to home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- Thank-you / post-signup screen ----------
  if (submitted) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: COLORS.bg }}>
        <SectionWrap>
          <Card className="border-0" style={{ backgroundColor: COLORS.card }}>
            <CardContent className="p-8 md:p-12 text-center">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(34,197,94,0.15)' }}
              >
                <CheckCircle2 className="h-8 w-8" style={{ color: COLORS.success }} />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.text }}>
                {config?.thank_you_headline || 'You\'re on the list, builder.'}
              </h1>
              <p className="text-base md:text-lg mb-6" style={{ color: COLORS.textMuted }}>
                {config?.thank_you_body}
              </p>

              {!form.wallet_trc20 && (
                <div
                  className="text-left rounded-xl p-4 mb-6 border"
                  style={{ backgroundColor: COLORS.bgAlt, borderColor: COLORS.border }}
                  data-testid="wallet-notice"
                >
                  <div className="flex items-start gap-3">
                    <Wallet className="h-5 w-5 mt-0.5" style={{ color: COLORS.accent }} />
                    <div>
                      <p className="font-medium" style={{ color: COLORS.text }}>
                        Wallet not yet on file
                      </p>
                      <p className="text-sm mt-1" style={{ color: COLORS.textMuted }}>
                        BBC Quick List uses direct member-to-member crypto payments and
                        Pay-It-Forward. You can add your TRON TRC-20 wallet later from your
                        limited back-office. We'll remind you before payments are needed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div
                className="text-left rounded-xl p-5 mb-6 border"
                style={{ backgroundColor: COLORS.bgAlt, borderColor: COLORS.border }}
              >
                <p className="text-sm mb-2" style={{ color: COLORS.textMuted }}>
                  Your personal referral link
                </p>
                <code
                  className="block p-3 rounded-lg text-sm break-all"
                  style={{ backgroundColor: COLORS.bg, color: COLORS.accent }}
                  data-testid="thank-you-referral-link"
                >
                  {submitted.referral_link}
                </code>
                <Button
                  className="mt-3"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard?.writeText(submitted.referral_link);
                    toast.success('Referral link copied');
                  }}
                  style={{ borderColor: COLORS.accent, color: COLORS.accent }}
                  data-testid="thank-you-copy-btn"
                >
                  Copy referral link
                </Button>
              </div>

              <Button
                size="lg"
                onClick={() => navigate('/waitlist-dashboard')}
                style={{ backgroundColor: COLORS.accent, color: COLORS.bg }}
                data-testid="enter-limited-dashboard-btn"
              >
                Enter my limited back-office
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </SectionWrap>

        {/* Before You Join — read this first */}
        <BeforeYouJoinFAQ variant="dark" defaultOpen={5} />
      </div>
    );
  }

  // ---------- Main funnel page ----------
  const t2 = config?.touch_2;
  const t3 = config?.touch_3;
  const t4 = config?.touch_4;

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.bg, color: COLORS.text }}>
      {/* Hero */}
      <section
        className="relative overflow-hidden pt-5 pb-10 md:py-24"
        style={{ backgroundColor: COLORS.bg }}
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background: `radial-gradient(60% 60% at 50% 0%, ${COLORS.accent}22, transparent 60%)`,
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 md:px-6 text-center">
          <div className="mb-6 md:mb-12 text-left">
            <BBCQuickListBrandHero
              primary="Quick List"
              secondary="Waiting List"
              tagline="Infrastructure Beats Hype"
              subline="Build Your List Before Launch"
              dataTestId="waitlist-brand-hero"
            />
          </div>

          {referrer ? (
            <Badge
              className="mb-5 px-3 py-1"
              style={{
                backgroundColor: 'rgba(34,197,94,0.12)',
                color: COLORS.success,
                border: `1px solid ${COLORS.success}40`,
              }}
              data-testid="referrer-badge"
            >
              <UserCheck className="h-3 w-3 mr-1.5 inline" />
              Invited by {referrer.sponsor_name}
            </Badge>
          ) : (
            <Badge
              className="mb-5 px-3 py-1"
              style={{
                backgroundColor: COLORS.accentSoft,
                color: COLORS.accent,
                border: `1px solid ${COLORS.accent}40`,
              }}
            >
              <Sparkles className="h-3 w-3 mr-1.5 inline" />
              {config?.hero_eyebrow || 'Early Builder Access'}
            </Badge>
          )}

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-5"
            style={{ color: COLORS.text }}
          >
            {config?.hero_headline}
          </h1>
          <p className="text-base md:text-lg mb-8 max-w-2xl mx-auto" style={{ color: COLORS.textMuted }}>
            {config?.hero_subheadline}
          </p>
          <div className="flex flex-wrap gap-3 justify-center" style={{ color: COLORS.textMuted }}>
            <span className="text-xs uppercase tracking-wider">Infrastructure beats hype</span>
            <span style={{ color: COLORS.border }}>·</span>
            <span className="text-xs uppercase tracking-wider">Builders, not complainers</span>
            <span style={{ color: COLORS.border }}>·</span>
            <span className="text-xs uppercase tracking-wider">Early builder access</span>
          </div>
        </div>
      </section>

      {/* Touch 2 (always visible) */}
      <SectionWrap alt>
        <TouchPanel
          touch={t2}
          badge="Touch 2"
          onContinue={continueToT3}
          dataTestId="touch-2"
        />
      </SectionWrap>

      {/* Touch 3 (revealed) */}
      {revealed.t3 && (
        <SectionWrap>
          <div id="touch-3">
            <TouchPanel
              touch={t3}
              badge="Touch 3"
              onContinue={continueToT4}
              dataTestId="touch-3"
            />
          </div>
        </SectionWrap>
      )}

      {/* Touch 4 (revealed) */}
      {revealed.t4 && (
        <SectionWrap alt>
          <div id="touch-4">
            <TouchPanel
              touch={t4}
              badge="Touch 4"
              isFinal
              formRef={formRef}
              dataTestId="touch-4"
            />
          </div>
        </SectionWrap>
      )}

      {/* Registration form */}
      <SectionWrap>
        <div ref={formRef} />
        <Card className="border-0" style={{ backgroundColor: COLORS.card }}>
          <div
            className="p-6 md:p-8 border-b"
            style={{ borderColor: COLORS.border }}
          >
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: COLORS.text }}>
              {config?.form_title || 'Reserve Your Builder Spot'}
            </h2>
            <p className="text-sm md:text-base mt-2" style={{ color: COLORS.textMuted }}>
              {referrer
                ? `Your sponsor: ${referrer.sponsor_name}. Your spot will hold in their Personal Pay-It-Forward list.`
                : 'No referral code — your spot will be placed in the Global Pay-It-Forward list.'}
            </p>
          </div>
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="waitlist-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>
                    First name *
                  </label>
                  <Input
                    type="text"
                    required
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="border-0"
                    style={{ backgroundColor: COLORS.bgAlt, color: COLORS.text }}
                    data-testid="wl-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>
                    Last name *
                  </label>
                  <Input
                    type="text"
                    required
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="border-0"
                    style={{ backgroundColor: COLORS.bgAlt, color: COLORS.text }}
                    data-testid="wl-last-name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>
                  Email *
                </label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="border-0"
                  style={{ backgroundColor: COLORS.bgAlt, color: COLORS.text }}
                  data-testid="wl-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>
                  Mobile phone *
                </label>
                <Input
                  type="tel"
                  required
                  value={form.mobile_phone}
                  onChange={(e) => setForm({ ...form, mobile_phone: e.target.value })}
                  placeholder="+1 555 123 4567"
                  className="border-0"
                  style={{ backgroundColor: COLORS.bgAlt, color: COLORS.text }}
                  data-testid="wl-mobile"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>
                  TRON TRC-20 wallet address (optional)
                </label>
                <Input
                  type="text"
                  value={form.wallet_trc20}
                  onChange={(e) => setForm({ ...form, wallet_trc20: e.target.value })}
                  placeholder="T... (you can add this later)"
                  className="border-0"
                  style={{ backgroundColor: COLORS.bgAlt, color: COLORS.text }}
                  data-testid="wl-wallet"
                />
                <p className="text-xs mt-2" style={{ color: COLORS.textMuted }}>
                  Optional — you can add or update this from your limited back-office.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="wallet-help"
                  checked={form.needs_wallet_help}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, needs_wallet_help: checked === true })
                  }
                  data-testid="wl-wallet-help"
                />
                <label htmlFor="wallet-help" className="text-sm cursor-pointer" style={{ color: COLORS.text }}>
                  {config?.wallet_help_label}
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={form.consent_communications}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, consent_communications: checked === true })
                  }
                  data-testid="wl-consent"
                />
                <label htmlFor="consent" className="text-sm cursor-pointer" style={{ color: COLORS.text }}>
                  {config?.consent_label}
                </label>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full py-6 text-base font-semibold rounded-xl"
                style={{ backgroundColor: COLORS.accent, color: COLORS.bg }}
                data-testid="wl-submit"
              >
                {submitting ? 'Saving your spot…' : t4?.cta_button_text || 'Reserve my builder spot'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>

              <p className="text-xs text-center" style={{ color: COLORS.textMuted }}>
                <Shield className="h-3 w-3 inline mr-1" />
                {config?.disclaimer_text}
              </p>
            </form>
          </CardContent>
        </Card>
      </SectionWrap>

      {/* Before You Join — compliance-friendly FAQ */}
      <BeforeYouJoinFAQ variant="dark" />

      {/* Footer */}
      <footer className="py-8 text-center text-xs" style={{ color: COLORS.textMuted }}>
        &copy; {new Date().getFullYear()} BBC Quick List · Infrastructure beats hype.
        <div className="mt-2 flex justify-center gap-4">
          <Link to="/terms" className="hover:underline">Terms</Link>
          <Link to="/privacy-policy" className="hover:underline">Privacy</Link>
        </div>
      </footer>
    </div>
  );
};

export default WaitlistLaunchPage;
